/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Suspense, type HTMLAttributes } from 'react';
import { Await } from 'react-router';
import { Region } from '@/components/region';
import { SEARCH_POPUP_REGION_ID } from '@/lib/page-designer/search-popup-page';
import type { PageWithComponentData } from '@/lib/page-designer/page-loader.server';

export interface SearchPopupPageRegionProps extends HTMLAttributes<HTMLDivElement> {
    page: Promise<PageWithComponentData | null> | undefined;
}

export function SearchPopupPageRegion({ page, ...rest }: SearchPopupPageRegionProps) {
    if (!page) {
        return null;
    }

    return (
        <Suspense fallback={null}>
            <Await resolve={page}>
                {(resolved) =>
                    resolved ? (
                        <Region page={resolved} regionId={SEARCH_POPUP_REGION_ID} {...rest} />
                    ) : null
                }
            </Await>
        </Suspense>
    );
}
